import React, { lazy, Suspense } from 'react';
import { Flex, Card, Table, Text, Progress } from "@radix-ui/themes";
import { OrgUser } from './OrgUserRecords';
import { OrgInvite } from './OrgInvitationRecords';
import { useProfileContext } from "context";
import { useGetOrgUsers, useEditOrgUser, useDeleteOrgUser, useGetInviteUsers, useResendInviteUser, useDeleteInviteUser } from "hooks";
import { ToastAlert, TruncatedText } from "components";

const InviteUserModal = lazy(() => import("./InviteUserModal"));

const OrganisationTeam = ({}) => {

  const { userProfile } = useProfileContext();
  const viewerId = userProfile.user_id;
  const organisationId = userProfile.organisation_id;
  const { data: orgUser, refetch: refetchOrgUsers, isLoading: isOrgUsersLoading } = useGetOrgUsers(organisationId);
  const orgUserList = orgUser?.map((entry) => ({
    id: entry.user.id,
    email: entry.user.email,
    first_name: entry.user.first_name,
    last_name: entry.user.last_name,
    user_role: entry.user.user_role,
    created_at: entry.user.created_at,
    last_login: entry.user.last_login,
    locations: entry.locations,
  }));

  const { data: invitedUser, refetch: refetchInvitedUsers, isLoading: isInvitedUsersLoading } = useGetInviteUsers();
  const invitedUserList = invitedUser?.map((entry) => entry);

  const { mutate: editUser } = useEditOrgUser();
  const { mutate: deleteUser } = useDeleteOrgUser();

  const handleOrgUserChange = (value, orgUserId, type, additionalData = {}) => {
    if (value != null && (typeof value !== 'string' || value.trim() !== "")) {
      const requestData = { 
        id: orgUserId, 
        [type]: value,
        ...additionalData 
      };
      
      editUser({
        owner_id: viewerId,
        data: requestData
      });
    }
  };

  const handleOrgUserDelete = (viewerId, orgUserId) => {
    deleteUser({
      owner_id: viewerId,
      data: { id: orgUserId }
    });
    refetchOrgUsers();
    refetchInvitedUsers();
  };

  const { mutate: resendInvite } = useResendInviteUser();
  const handleOrgInviteResend = (email, token) => {
    let payload = { email: email, token: token }
    resendInvite(payload);
    refetchOrgUsers();
    refetchInvitedUsers();
  };

  const { mutate: deleteInvite } = useDeleteInviteUser();
  const handleOrgInviteDelete = (token) => {
    let payload = { token: token }
    deleteInvite(payload);
    refetchOrgUsers();
    refetchInvitedUsers();
  };

  if (isOrgUsersLoading || isInvitedUsersLoading) {
    return (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1000
        }}>
          <Progress size="1" duration="1" />
        </div>
    );
  }

    return (
    <>
      <Flex className="team-details-container" direction="column" gap="5"
        style={{alignItems: 'flex-start', flex: '1 0 0', alignSelf: 'stretch', overflow: 'scroll', height: '100%'}}>
        {userProfile?.user_role !== 'Manager' && (
          <Flex justify="end" width="100%">
            <Suspense>
              <InviteUserModal />
            </Suspense>
          </Flex>
        )}
        {/* Team Details */}
        <Card style={{ display: 'flex', flexDirection: "column", alignItems: "flex-start", gap: "var(--space-4)",
          alignSelf: "stretch", flex: '1 0 0', padding: 'var(--space-4)', height: '100%'}}>
          <Table.Root layout="fixed" style={{ width: "100%", minWidth: 0, height: "100%" }}>
            <Table.Header style={{  position:"sticky", top: "0", zIndex: "1", backgroundColor: 'var(--color-panel-solid)'}}>
              <Table.Row align="center">
                  <Table.ColumnHeaderCell style={{width: "18%"}}>
                    <TruncatedText size="2" weight="medium">Name</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{width: "18%"}}>
                    <TruncatedText size="2" weight="medium">Role</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{width: "18%"}}>
                    <TruncatedText size="2" weight="medium">Brands & Locations</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{width: "18%", minWidth: 0}}>
                    <TruncatedText weight="medium">Created On</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{width: "18%", minWidth: 0}}>
                    <TruncatedText weight="medium">Last Logged In</TruncatedText>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell style={{width: "10%"}}>
                    <Text size="2" weight="medium"></Text>
                  </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {orgUserList
                ?.sort((a, b) => {
                  const firstNameComparison = (a.first_name || "").localeCompare(b.first_name || "");
                  if (firstNameComparison !== 0) {
                    return firstNameComparison;
                  }
                  return (a.last_name || "").localeCompare(b.last_name || "");
                })
                .map((entry, index) => (
                  <OrgUser
                    key={index}
                    orgUserId={entry.id}
                    viewerId={viewerId}
                    entry={entry}
                    handleOrgUserChange={handleOrgUserChange}
                    handleOrgUserDelete={handleOrgUserDelete}
                  />
                ))}
              {invitedUserList
                ?.sort((a, b) => (a.email || "").localeCompare(b.email || ""))
                .map((entry, index) => (
                  <OrgInvite 
                    key={index}
                    email={entry.email}
                    token={entry.token}
                    role={entry.invitation_type}
                    locations={entry.locations_list}
                    brands={entry.brands_list || []}
                    handleOrgInviteResend={handleOrgInviteResend}
                    handleOrgInviteDelete={handleOrgInviteDelete}
                    viewerId={viewerId}
                  />
                ))}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
      {/* Toast Updates */}
      <ToastAlert />
    </>
  );
};

export default OrganisationTeam;
